import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm, useStore } from '@tanstack/react-form';
import { Box, Alert, Snackbar } from '@mui/material';
import type { DetailMetadata, DetailFormCustomFieldRenderer, RichTextOptions } from './types';
import type { EntityField, LookupDataItem, SaveEntityRequest } from '../grid';
import {
  useSaveEntityMutation,
  useDeleteEntityMutation,
  useArchiveEntityMutation,
} from '../grid/api/gridApi';
import { DetailFormBlock } from './DetailFormBlock';
import { DetailFormActions } from './DetailFormActions';
import { DeleteConfirmDialog } from '../../components/common/DeleteConfirmDialog';
import { ArchiveConfirmDialog } from '../../components/common/ArchiveConfirmDialog';
import { PreSaveContext, usePreSaveProvider } from './PreSaveContext';
import { extractErrorMessage } from '../../utils/errorUtils';
import { normalizeMediaBeforeSave } from './richTextMediaHelpers';
import { formatFieldValueForSave } from '../grid/utils';
import { RelationPickerDialog } from '../grid/components/dialogs/RelationPickerDialog';
import { SalaryCalculateDialog } from './SalaryCalculateAction';
import { useDetailFormRelations } from './useDetailFormRelations';
import { buildFieldOccurrenceResolver } from './resolveFieldOccurrences';
import type { Command } from '../menu/types';
import { getCommandId, hasCommand } from '../grid/commands/utils';
import { useDetailCommandAvailability } from '../grid/commands/useDetailCommandAvailability';
import { useMySelfReviewForm } from '../employee-review/hooks/useMySelfReviewForm';
import { useEmployeeReviewCommandAvailability } from '../employee-review/hooks/useEmployeeReviewCommandAvailability';
import { renderEmployeeReviewRatingField } from '../employee-review/components/EmployeeReviewRatingField';
import { EmployeeReviewFeedbackDoneControl } from '../employee-review/components/EmployeeReviewFeedbackDoneControl';
import { createFinanceEmailAttachmentsFieldRenderer } from './createFinanceEmailAttachmentsFieldRenderer';
import { ImplementWithAiDialog } from '../ai-implementation/components/ImplementWithAiDialog';
import { ConfigureAiProviderDialog } from '../ai-implementation/components/ConfigureAiProviderDialog';
import { ProfilePhotoEditor } from '../profile-photo/ProfilePhotoEditor';
import {
  getDetailFormActionCommands,
  getProfilePhotoInitials,
  getProfilePhotoRelationId,
  PROFILE_PHOTO_ANCHOR_FIELD,
  PROFILE_PHOTO_COMMAND_ID,
} from '../profile-photo/profilePhotoDetailForm';
import { GenerateAutoInvoiceDialog } from '../grid/components/dialogs/GenerateAutoInvoiceDialog';
import SendInvoiceToClientDialog from '../grid/components/dialogs/SendInvoiceToClientDialog';
import SendSimpleOutboundDialog from '../grid/components/dialogs/SendSimpleOutboundDialog';
import { usePrepareInvoiceDeliveryPreviewMutation } from '../grid/api/invoiceDeliveryApi';
import {
  usePrepareSimpleOutboundPreviewMutation,
  usePrepareSimpleOutboundReplyPreviewMutation,
} from '../grid/api/simpleOutboundApi';
import {
  EMPLOYEE_REVIEW_CONTEXT_JS_METHOD,
  EMPLOYEE_PHOTO_JS_METHOD,
  SELF_PROFILE_PHOTO_JS_METHOD,
  JsMethodDetailFormBindings,
} from '../js-methods';
import { JsMethodLoadingProvider } from '../js-methods/jsMethodLoadingProvider';
import { JsMethodLoadingOverlay } from '../js-methods/JsMethodLoadingOverlay';
import { DatePickerMaxDateContext, getEndOfToday } from '../../components/common/datePickerMaxDate';

type DetailFormProps = {
  metadata: DetailMetadata;
  initialValues: Record<string, unknown>;
  mode: 'add' | 'edit';
  basePath: string;
  lookupMaps?: Record<string, Record<string, string>>;
  lookupOptionMaps?: Record<string, Record<string, string>>;
  lookupDataArrays?: Record<string, LookupDataItem[]>;
  parentId?: string;
  detailFormKey?: string;
  parentGridKey?: string;
  menuItemId?: number;
  showArchive?: boolean;
  showDelete?: boolean;
  onSuccess?: () => void;
  onSaveSuccess?: () => void;
  suppressPerformReload?: boolean;
  onAfterCreate?: (
    entityId: string,
    values: Record<string, unknown>
  ) => Promise<boolean | void> | boolean | void;
  onDismiss?: () => void;
  onCustomSubmit?: (values: Record<string, unknown>) => void | Promise<void>;
  pruneEmptyTabs?: boolean;
  richTextOptions?: RichTextOptions;
  cancelBehavior?: 'navigate' | 'reset';
  linkToParentId?: string;
  linkToParentDetailFormKey?: string;
  title?: string;
  initialDateRange?: { startDate?: string; endDate?: string };
  dataOptionsMax?: boolean;
};

type PendingEntityAction = 'archive' | 'delete' | null;

export const DetailForm = ({
  metadata,
  initialValues: rawInitialValues,
  mode,
  basePath,
  lookupMaps,
  lookupOptionMaps,
  lookupDataArrays,
  parentId,
  detailFormKey,
  parentGridKey,
  menuItemId,
  showArchive = false,
  showDelete = false,
  onSuccess,
  onSaveSuccess,
  suppressPerformReload = false,
  onAfterCreate,
  onDismiss,
  onCustomSubmit,
  pruneEmptyTabs = false,
  richTextOptions,
  cancelBehavior = 'navigate',
  linkToParentId,
  linkToParentDetailFormKey,
  title,
  initialDateRange,
  dataOptionsMax,
}: DetailFormProps) => {
  const { initialValues, handleSaveSuccess: handleMySelfReviewSaveSuccess } = useMySelfReviewForm(
    metadata,
    rawInitialValues,
    basePath,
    parentGridKey
  );
  const navigate = useNavigate();
  const [saveEntity] = useSaveEntityMutation();
  const [deleteEntity] = useDeleteEntityMutation();
  const [archiveEntity] = useArchiveEntityMutation();
  const [formError, setFormError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const saveCommandRef = useRef<Command | undefined>(undefined);
  const preSave = usePreSaveProvider();
  const [isActioning, setIsActioning] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingEntityAction>(null);
  const isDeleted = Boolean(initialValues.DeletedAt);
  const [saveSnackbarOpen, setSaveSnackbarOpen] = useState(false);
  const [prepareInvoiceDeliveryPreview] = usePrepareInvoiceDeliveryPreviewMutation();
  const [prepareSimpleOutboundPreview] = usePrepareSimpleOutboundPreviewMutation();
  const [prepareSimpleOutboundReplyPreview] = usePrepareSimpleOutboundReplyPreviewMutation();
  const commandAvailability = useDetailCommandAvailability(metadata.commands, initialValues.Id);

  const normalizedInitialValues = useMemo(() => JSON.stringify(initialValues), [initialValues]);
  const normalizeValuesForSave = (values: Record<string, unknown>): Record<string, unknown> =>
    Object.fromEntries(
      Object.entries(values).map(([name, val]) => [
        name,
        typeof val === 'string' ? normalizeMediaBeforeSave(val) : val,
      ])
    );

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: async () => {
      const saveCommand = saveCommandRef.current;
      setFormError(null);
      try {
        await preSave.runAll();
        const latestValues = normalizeValuesForSave(form.state.values as Record<string, unknown>);

        if (onCustomSubmit) {
          await onCustomSubmit(latestValues);

          const successHandler = saveCommand?.closeForm
            ? (onSuccess ?? (() => navigate({ to: basePath })))
            : (onSaveSuccess ?? onSuccess ?? (() => navigate({ to: basePath })));

          successHandler();
          return;
        }

        const fieldMetaByName = new Map(
          metadata.fields.map((field) => [field.dataField || field.field, field])
        );

        const fields: EntityField[] = Object.entries(latestValues)
          .filter(([name]) => {
            if (name === 'Id' || name === 'DeletedAt') {
              return false;
            }

            const fieldMetadata = fieldMetaByName.get(name);
            return mode === 'add'
              ? fieldMetadata?.canCreate !== false
              : fieldMetadata?.canEdit !== false;
          })
          .map(([name, val]) => ({
            FieldName: name,
            StringValue: formatFieldValueForSave(val, fieldMetaByName.get(name)),
          }));

        const saveRequest: SaveEntityRequest = {
          NodeKey: metadata.contentKey,
          Fields: fields,
          invalidateEntity: mode === 'edit' && latestValues.Id != null,
          gridNodeKeyToInvalidate: parentGridKey,
        };

        if (mode === 'add' && linkToParentId) {
          saveRequest.ParentId = linkToParentDetailFormKey
            ? `${linkToParentDetailFormKey}#|#${linkToParentId}`
            : linkToParentId;
        }

        if (mode === 'edit' && latestValues.Id != null) {
          saveRequest.Id = String(latestValues.Id);
        }

        const saveResult = await saveEntity(saveRequest).unwrap();
        if (saveResult.data?.Code !== 1) {
          throw new Error(saveResult.data?.Error || saveResult.message || 'Failed to save entity');
        }

        const dataArray = (saveResult.data as Record<string, unknown>)?.Data as
          Array<Record<string, unknown>> | undefined;
        const newId = dataArray?.[0]?.Id;
        if (mode === 'add' && newId != null && onAfterCreate) {
          const shouldContinue = await onAfterCreate(String(newId), latestValues);
          if (shouldContinue === false) return;
        }

        if (saveCommand?.closeForm) {
          navigateAway({ preferCachedOnMount: true });
          return;
        }

        if (saveCommand?.performReload === true && !suppressPerformReload) {
          if (mode === 'add' && newId != null) {
            const detailPath = basePath.endsWith('/')
              ? `${basePath}${newId}`
              : `${basePath}/${newId}`;
            const targetUrl = `${detailPath}${window.location.search}${window.location.hash}`;
            window.history.replaceState(window.history.state, '', targetUrl);
          }

          window.location.reload();
          return;
        }

        if (handleMySelfReviewSaveSuccess(saveResult)) return;

        if (onSaveSuccess) {
          onSaveSuccess();
          setSaveSnackbarOpen(true);
          return;
        }

        if (onSuccess) {
          onSuccess();
          return;
        }

        if (mode === 'edit') {
          setSaveSnackbarOpen(true);
          return;
        }

        if (newId) {
          await navigate({ to: `${basePath}/${newId}` });
        } else {
          setSaveSnackbarOpen(true);
          form.reset(latestValues);
        }
      } catch (error) {
        setFormError(extractErrorMessage(error, 'Failed to save entity'));
      } finally {
        saveCommandRef.current = undefined;
      }
    },
  });

  useEffect(() => {
    form.reset(initialValues);
  }, [form, initialValues, normalizedInitialValues]);

  const showCancel = cancelBehavior !== 'reset' && Boolean(onDismiss);

  const isDirty = useStore(form.store, (state) => state.isDirty);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const currentValues = useStore(form.store, (state) => state.values) as Record<string, unknown>;

  const handleNavigate = (url: string) => window.location.assign(url);
  const currentPath = window.location.pathname + window.location.search + window.location.hash;
  const maxDate = dataOptionsMax === true ? getEndOfToday() : undefined;

  const {
    relationPickerCommand,
    salaryCalculateCommand,
    relationTargets,
    registerRelationTarget,
    handleDetailCommand,
    closeRelationPicker,
    closeSalaryCalculatePicker,
    aiImplementationTaskId,
    closeAiImplementationDialog,
    isAiProviderDialogOpen,
    closeAiProviderDialog,
    autoInvoiceModal,
    sendInvoiceModal,
    sendSimpleOutboundModal,
    closeAutoInvoiceModal,
    closeSendInvoiceModal,
    closeSendSimpleOutboundModal,
  } = useDetailFormRelations({
    commands: metadata.commands,
    parentId,
    entityId: initialValues.Id == null ? undefined : String(initialValues.Id),
    entityData: currentValues,
    hasUnsavedChanges: isDirty,
    navigate: (path) => {
      navigate({ to: typeof path === 'number' ? '..' : path });
    },
    onError: setFormError,
    onSuccess: setSuccessMessage,
    onNavigate: handleNavigate,
    currentPath,
    prepareInvoiceDeliveryPreview: async (invoiceId, templateId) =>
      prepareInvoiceDeliveryPreview({ invoiceId, templateId }).unwrap(),
    prepareSimpleOutboundPreview: async (clientId) =>
      prepareSimpleOutboundPreview(clientId).unwrap(),
    prepareSimpleOutboundReplyPreview: async ({ historyId, replyAll }) =>
      prepareSimpleOutboundReplyPreview({ historyId, replyAll }).unwrap(),
  });

  const effectiveRichTextOptions = useMemo(() => {
    const initialEntityId =
      typeof initialValues.Id === 'string' || typeof initialValues.Id === 'number'
        ? initialValues.Id
        : undefined;

    return {
      ...richTextOptions,
      uploadContextId: richTextOptions?.uploadContextId ?? initialEntityId ?? parentId,
    };
  }, [initialValues.Id, parentId, richTextOptions]);

  const resolveFieldOccurrence = useMemo(
    () => buildFieldOccurrenceResolver(metadata.layout?.blocks, metadata.fields),
    [metadata.layout?.blocks, metadata.fields]
  );

  const hasWorkingHoursField = useMemo(
    () =>
      metadata.fields.some(
        (field) => field.dataType === 'zReactNode' && field.renderingClass === 'workingHours'
      ),
    [metadata.fields]
  );
  const hasUnsavedChanges = isDirty || hasWorkingHoursField;
  const isParticipantFeedbackForm = hasCommand(
    metadata.commands,
    'employeereviewparticipantfeedbacksubmit'
  );
  const employeeReviewId = Number(initialValues.Id ?? parentId);

  const handleEmployeeReviewStatusChanged = (status: number) => {
    form.reset({ ...currentValues, Status: status });
    onSaveSuccess?.();
  };

  const isFinanceEmailHistoryForm =
    (metadata.dataSource || '').toLowerCase() === 'crmfinanceemailhistory';
  // Prefer route id (o9 / i5); entity.Id can be missing/casing-dependent after GetOne.
  const financeEmailHistoryId = String(
    parentId || initialValues.Id || (initialValues as { id?: unknown }).id || ''
  );
  const financeEmailAttachmentsRenderer = useMemo(
    () =>
      isFinanceEmailHistoryForm
        ? createFinanceEmailAttachmentsFieldRenderer(financeEmailHistoryId)
        : undefined,
    [financeEmailHistoryId, isFinanceEmailHistoryForm]
  );
  const customFieldRenderer = useMemo((): DetailFormCustomFieldRenderer | undefined => {
    if (!isParticipantFeedbackForm && !financeEmailAttachmentsRenderer) {
      return undefined;
    }

    return (props) => {
      const financeEmailField = financeEmailAttachmentsRenderer?.(props);
      if (financeEmailField !== undefined) {
        return financeEmailField;
      }

      if (isParticipantFeedbackForm) {
        return renderEmployeeReviewRatingField(props);
      }

      return undefined;
    };
  }, [financeEmailAttachmentsRenderer, isParticipantFeedbackForm]);

  const profilePhotoRelationId = hasCommand(metadata.commands, PROFILE_PHOTO_COMMAND_ID)
    ? getProfilePhotoRelationId(initialValues, parentId)
    : null;
  const detailFormActionCommands = useMemo(
    () => getDetailFormActionCommands(metadata.commands),
    [metadata.commands]
  );
  const employeeReviewCommandAvailability = useEmployeeReviewCommandAvailability(
    metadata.commands,
    initialValues.Id
  );
  const hasEmployeePhotoJsMethod = metadata.fields.some(
    (field) => field.nameJsMethod?.trim() === EMPLOYEE_PHOTO_JS_METHOD
  );
  const hasProfilePhotoJsMethod = metadata.fields.some(
    (field) => field.nameJsMethod?.trim() === SELF_PROFILE_PHOTO_JS_METHOD
  );
  const hasEmployeeReviewFeedbackContextJsMethod = metadata.fields.some(
    (field) => field.nameJsMethod?.trim() === EMPLOYEE_REVIEW_CONTEXT_JS_METHOD
  );
  const renderBeforeField =
    profilePhotoRelationId ||
    hasEmployeePhotoJsMethod ||
    hasProfilePhotoJsMethod ||
    hasEmployeeReviewFeedbackContextJsMethod
      ? ({ fieldName }: { fieldName: string }) => (
          <>
            {profilePhotoRelationId &&
            !hasEmployeePhotoJsMethod &&
            !hasProfilePhotoJsMethod &&
            fieldName === PROFILE_PHOTO_ANCHOR_FIELD ? (
              <ProfilePhotoEditor
                relationId={profilePhotoRelationId}
                initials={getProfilePhotoInitials(currentValues)}
              />
            ) : null}
            <JsMethodDetailFormBindings
              fields={metadata.fields}
              form={form}
              contentKey={metadata.contentKey}
              parentId={parentId}
              fieldName={fieldName}
            />
          </>
        )
      : undefined;

  const navigateAway = ({
    preferCachedOnMount = false,
  }: { preferCachedOnMount?: boolean } = {}) => {
    if (onSuccess) {
      onSuccess();
      return;
    }

    if (preferCachedOnMount) {
      try {
        const normalizedBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
        sessionStorage.setItem(`GRID_FAST_RETURN:${normalizedBasePath}`, '1');
      } catch (error) {
        console.warn('DetailForm: failed to set GRID_FAST_RETURN marker', {
          basePath,
          error,
        });
      }
    }

    navigate({ to: basePath });
  };

  const handleClose = () => {
    if (cancelBehavior === 'reset') {
      form.reset(initialValues);
      setFormError(null);
      return;
    }

    if (form.state.isDirty) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) return;
    }

    if (onDismiss) {
      onDismiss();
      return;
    }

    navigateAway({ preferCachedOnMount: true });
  };

  const runEntityAction = async ({
    apiCall,
    errorFallback,
  }: {
    apiCall: (args: { id: string; nodeKey: string }) => {
      unwrap: () => Promise<{ data?: { Code: number; Error?: string } | null; message?: string }>;
    };
    errorFallback: string;
  }) => {
    const id = initialValues.Id;
    if (!id) return;
    setIsActioning(true);
    try {
      const result = await apiCall({ id: String(id), nodeKey: metadata.contentKey }).unwrap();
      if (result.data?.Code !== 1) {
        throw new Error(result.data?.Error || result.message || errorFallback);
      }
      navigateAway();
    } catch (error) {
      setFormError(extractErrorMessage(error, errorFallback));
    } finally {
      setIsActioning(false);
    }
  };

  const handleDelete = () => setPendingAction('delete');
  const handleArchive = () => setPendingAction('archive');

  const handleConfiguredCommand = (command: Command) => {
    const commandId = getCommandId(command).toLowerCase();
    if (commandId === 'archive' && showArchive) {
      handleArchive();
      return;
    }
    if (commandId === 'delete' && showDelete) {
      handleDelete();
      return;
    }

    handleDetailCommand(command);
  };

  const handleConfirmPendingAction = async () => {
    const action = pendingAction;
    setPendingAction(null);
    if (action === 'delete') {
      await runEntityAction({
        apiCall: deleteEntity,
        errorFallback: 'Failed to delete entity',
      });
    } else if (action === 'archive') {
      await runEntityAction({
        apiCall: archiveEntity,
        errorFallback: 'Failed to archive entity',
      });
    }
  };

  return (
    <PreSaveContext.Provider value={preSave}>
      <JsMethodLoadingProvider>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            bgcolor: 'background.default',
          }}
        >
          <JsMethodDetailFormBindings
            fields={metadata.fields}
            form={form}
            contentKey={metadata.contentKey}
            parentId={parentId}
          />

          <DetailFormActions
            title={title}
            commands={detailFormActionCommands}
            isDeleted={isDeleted}
            onSave={(command) => {
              saveCommandRef.current = command;
              form.handleSubmit();
            }}
            onClose={showCancel ? handleClose : undefined}
            onConfiguredClose={() => navigateAway({ preferCachedOnMount: true })}
            isSubmitting={isSubmitting}
            isActioning={isActioning}
            isDirty={hasUnsavedChanges}
            onCommandClick={handleConfiguredCommand}
            isCommandDisabled={(command) =>
              commandAvailability.isDisabled(command) ||
              employeeReviewCommandAvailability.isDisabled(command) ||
              (['replytofinanceemail', 'addfinancemailsendertospamlist'].includes(
                getCommandId(command).toLowerCase()
              ) &&
                String(currentValues.Direction ?? currentValues.direction ?? '')
                  .trim()
                  .toLowerCase() !== 'inbound')
            }
          />

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              pr: 1,
              position: 'relative',
              '&::-webkit-scrollbar': { width: 10 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: 8,
                border: '2px solid transparent',
                backgroundClip: 'padding-box',
              },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            }}
          >
            <JsMethodLoadingOverlay />
            {formError && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {formError}
              </Alert>
            )}
            {employeeReviewCommandAvailability.hasManagerReviewAction &&
              Number.isInteger(employeeReviewId) &&
              employeeReviewId > 0 && (
                <EmployeeReviewFeedbackDoneControl
                  reviewId={employeeReviewId}
                  disabled={isSubmitting || isActioning || hasUnsavedChanges}
                  onStatusChanged={handleEmployeeReviewStatusChanged}
                  onSuccess={setSuccessMessage}
                />
              )}

            <DatePickerMaxDateContext.Provider value={maxDate}>
              {metadata.layout?.blocks?.map((block) => (
                <DetailFormBlock
                  key={`${block.key}:${block.id}`}
                  block={block}
                  fields={metadata.fields}
                  form={form}
                  lookupMaps={lookupMaps}
                  lookupOptionMaps={lookupOptionMaps}
                  lookupDataArrays={lookupDataArrays}
                  disabled={isSubmitting}
                  mode={mode}
                  parentContentKey={metadata.contentKey}
                  parentId={parentId}
                  detailFormKey={detailFormKey}
                  parentGridKey={parentGridKey}
                  menuItemId={menuItemId}
                  pruneEmptyTabs={pruneEmptyTabs}
                  richTextOptions={effectiveRichTextOptions}
                  dataSource={metadata.dataSource}
                  onRegisterRelationTarget={registerRelationTarget}
                  resolveFieldOccurrence={resolveFieldOccurrence}
                  customFieldRenderer={customFieldRenderer}
                  renderBeforeField={renderBeforeField}
                  initialDateRange={initialDateRange}
                />
              ))}
            </DatePickerMaxDateContext.Provider>

            {!metadata.layout?.blocks && (
              <Alert severity="info">Form layout is not configured for this detail form.</Alert>
            )}
          </Box>
        </Box>
      </JsMethodLoadingProvider>

      <DeleteConfirmDialog
        open={pendingAction === 'delete'}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmPendingAction}
        loading={isActioning}
      />

      <ArchiveConfirmDialog
        open={pendingAction === 'archive'}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmPendingAction}
        loading={isActioning}
      />

      <RelationPickerDialog
        key={relationPickerCommand?.key ?? 'relation-picker-closed'}
        command={relationPickerCommand}
        parentId={String(parentId ?? initialValues.Id ?? '')}
        relationTargets={relationTargets}
        onClose={closeRelationPicker}
      />

      <SalaryCalculateDialog
        command={salaryCalculateCommand}
        parentId={String(parentId ?? initialValues.Id ?? '')}
        relationTargets={relationTargets}
        onClose={closeSalaryCalculatePicker}
        onError={setFormError}
      />

      <GenerateAutoInvoiceDialog
        payload={autoInvoiceModal}
        currentPath={currentPath}
        onNavigate={handleNavigate}
        onClose={closeAutoInvoiceModal}
      />

      {sendInvoiceModal && (
        <SendInvoiceToClientDialog
          key={`${sendInvoiceModal.invoiceId}-${sendInvoiceModal.templateId ?? 'default'}`}
          open
          invoiceId={sendInvoiceModal.invoiceId}
          templateId={sendInvoiceModal.templateId}
          preview={sendInvoiceModal.preview}
          onClose={closeSendInvoiceModal}
          onSuccess={setSuccessMessage}
        />
      )}

      {sendSimpleOutboundModal && (
        <SendSimpleOutboundDialog
          key={
            sendSimpleOutboundModal.preview.replyToHistoryId ??
            `send-${sendSimpleOutboundModal.clientId}`
          }
          open
          clientId={sendSimpleOutboundModal.clientId}
          preview={sendSimpleOutboundModal.preview}
          onClose={closeSendSimpleOutboundModal}
          onSuccess={setSuccessMessage}
        />
      )}

      <ImplementWithAiDialog
        open={aiImplementationTaskId != null}
        taskId={aiImplementationTaskId}
        onClose={closeAiImplementationDialog}
      />

      <ConfigureAiProviderDialog open={isAiProviderDialogOpen} onClose={closeAiProviderDialog} />

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="success"
          onClose={() => setSuccessMessage(null)}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={saveSnackbarOpen}
        autoHideDuration={3500}
        onClose={() => setSaveSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="success" onClose={() => setSaveSnackbarOpen(false)}>
          Data saved
        </Alert>
      </Snackbar>
    </PreSaveContext.Provider>
  );
};
